import { MomentInput } from "moment";
import { usePastDaysLimit } from "PFApp/hooks/use_past_days_limit";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking } from "PFTypes";
import { MutableRefObject, useEffect, useRef, useState } from "react";

import { useCalendarContext } from "../../../../context/calendar_context";
import getUpdatedDates from "./get_updated_dates";
import { Mode } from "./index";

interface Props {
  booking: Booking;
  left: number;
  width: number;
  draggedDays: MutableRefObject<number>;
  draggingMode: MutableRefObject<Mode | null>;
  calendarEndDate: string;
  onMouseMoveEnd: () => void;
  draggingDates: MutableRefObject<{ start: MomentInput; end: MomentInput }>;
  draggingOriginClickX: MutableRefObject<number | null>;
  rowRef: MutableRefObject<HTMLDivElement | null>;
}

const getNumberOfDraggedDays = (currentMouseX, originClickX, dayWidth, rowRef) => {
  const currentMouseXRelativelyToRow = currentMouseX - rowRef.current?.getBoundingClientRect().x;
  const delta = currentMouseXRelativelyToRow - originClickX;
  return Math.floor(delta / dayWidth);
};

const useOnMouseMove = ({
  booking,
  left,
  width,
  draggedDays,
  draggingMode,
  calendarEndDate,
  onMouseMoveEnd,
  draggingDates,
  draggingOriginClickX,
  rowRef
}: Props) => {
  const pastDaysLimit = usePastDaysLimit();
  const { utc, formatDatabaseDateTime } = useDateFormatter();
  const { dayWidth } = useCalendarContext();
  const [barDimensions, setBarDimensions] = useState<{ left: number; width: number }>({ left, width });

  const onMouseMove = (booking, left, width, dayWidth, calendarEndDate) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!draggingMode.current) {
      return;
    }
    const newDraggedDays = getNumberOfDraggedDays(
      event.clientX,
      draggingOriginClickX.current,
      dayWidth,
      rowRef
    );
    if (newDraggedDays === draggedDays.current) {
      return;
    }
    const { startDate, endDate, daysDiffLimited } = getUpdatedDates(
      draggingMode.current,
      newDraggedDays,
      booking?.start_date,
      booking?.end_date,
      calendarEndDate,
      utc,
      formatDatabaseDateTime,
      pastDaysLimit ?? 0
    );
    if (daysDiffLimited === draggedDays.current) {
      return;
    }
    setDimensions(newDraggedDays);
    draggedDays.current = newDraggedDays;
    draggingDates.current = {
      start: startDate,
      end: endDate
    };
    onMouseMoveEnd();
  };

  const setDimensions = (draggedDays) => {
    switch (draggingMode.current) {
      case "start_end":
        setBarDimensions({ left: left + draggedDays * dayWidth, width });
        return;
      case "start":
        setBarDimensions({
          left: left + draggedDays * dayWidth,
          width: width - draggedDays * dayWidth
        });
        return;
      case "end":
        setBarDimensions({ left: left, width: width + draggedDays * dayWidth });
        return;
      default:
        setInitialDimensions();
        return;
    }
  };

  const setInitialDimensions = () => {
    setBarDimensions({ left, width });
  };

  const onMouseMoveRef = useRef<EventListener>();

  useEffect(() => {
    onMouseMoveRef.current = onMouseMove(booking, left, width, dayWidth, calendarEndDate);
  }, [booking, left, width, dayWidth, calendarEndDate]);

  useEffect(() => {
    setBarDimensions({ left, width });
  }, [left, width, dayWidth]);

  return {
    onMouseMoveRef,
    barDimensions,
    setInitialDimensions
  };
};

export default useOnMouseMove;
